import './App.scss';
import {BrowserRouter, Switch, Route, Redirect, useLocation, useHistory} from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Totem from "./pages/Totem";
import Login from "./pages/Login";
import React, {useEffect, useState} from "react";
import {useStores} from "./stores/Context";
import ChoixEntreprise from "./components/ChoixEntreprise";
import NonAutorise from "./pages/NonAutorise";
import Catalogue from "./Catalogue";
import Mobile from "./Mobile";
import MonCompte from "./pages/MonCompte";
import Pricing from './pages/Pricing';
import AbonnementSuccess from './pages/AbonnementSuccess';
import MentionsLegales from './pages/MentionsLegales';

/**
 * ProtectedRoute — vérifie que l'espace courant est chargé avant d'afficher le contenu.
 * isLoading et isAuthorized sont locaux à ce composant, donc réinitialisés à chaque montage
 * (= à chaque changement de route), ce qui évite l'état figé du parent App.
 */
const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const history = useHistory();
    const {widgetStore, loginStore} = useStores();
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            const userCookie = widgetStore.getCookie("currentUserInfos");

            if (userCookie) {
                if (location.pathname === '/') {
                    const userInfos = JSON.parse(userCookie);
                    if (userInfos) {
                        if (userInfos.nb_entreprises > 1) {
                            history.push("/choixEspace");
                        } else if (userInfos.nb_entreprises === 1) {
                            const entreprises = await loginStore.loadEntreprises();
                            if (!cancelled && entreprises.length === 1) {
                                await loginStore.loadEntrepriseData(entreprises[0].code);
                                history.push('/app');
                            }
                        }
                    }
                } else {
                    // Vérifier que le cookie de l'espace courant existe
                    const espaceCookie = widgetStore.getCookie('currentEntrepriseInfos');
                    if (!espaceCookie) {
                        try {
                            const entreprises = await loginStore.loadEntreprises();
                            if (!cancelled) {
                                if (entreprises && entreprises.length > 0) {
                                    await loginStore.loadEntrepriseData(entreprises[0].code);
                                } else {
                                    setIsAuthorized(false);
                                }
                            }
                        } catch (error) {
                            if (!cancelled) setIsAuthorized(false);
                        }
                    }
                }
            } else {
                if (!cancelled) setIsAuthorized(false);
            }

            if (!cancelled) setIsLoading(false);
        };

        check();
        return () => { cancelled = true; };
    }, [location.pathname]);

    if (isLoading) return <div style={{ padding: '2rem', color: '#888' }}>Chargement...</div>;
    if (!isAuthorized) return <NonAutorise />;
    return children;
};

function App() {
    const {widgetStore, loginStore} = useStores();

    const [_token, setToken] = useState(widgetStore.getCookie("current_apiToken"));
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileOrTablet(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (isMobileOrTablet) {
        return <Mobile/>;
    }

    if (!loginStore.isUserConnected()) {
        return (
            <div>
                <BrowserRouter basename={process.env.REACT_APP_PATH_HOME_PAGE}>
                    <Switch>
                        <Route exact path="/pricing">
                            <Pricing />
                        </Route>
                        <Route exact path="/abonnement/success">
                            <AbonnementSuccess />
                        </Route>
                        <Route exact path="/mentions-legales">
                            <MentionsLegales/>
                        </Route>
                        <Route exact path="/mon-compte">
                            <Login setToken={setToken} show={true} ent="" />
                        </Route>
                        <Route exact path="/">
                            <Login setToken={setToken} show={true} ent="" />
                        </Route>
                        <Redirect to="/" />
                    </Switch>
                </BrowserRouter>
            </div>
        );
    }

    return (
        <BrowserRouter basename={process.env.REACT_APP_PATH_HOME_PAGE}>
            <Switch>
                {/* Routes statiques EN PREMIER */}
                <Route exact path="/choixEspace">
                    <ChoixEntreprise/>
                </Route>
                <Route exact path="/mon-compte">
                    <MonCompte/>
                </Route>
                <Route exact path="/pricing">
                    <Pricing />
                </Route>
                <Route exact path="/abonnement/success">
                    <AbonnementSuccess />
                </Route>
                <Route exact path="/catalogue">
                    <Catalogue/>
                </Route>
                <Route exact path="/mentions-legales">
                    <MentionsLegales/>
                </Route>
                <Route exact path="/entreprise-non-autorisee">
                    <NonAutorise contenu={"Vous n'avez pas accès à cet espace"}/>
                </Route>

                {/* Routes protégées — chaque route a son propre état isLoading/isAuthorized */}
                <Route exact path="/app/:id">
                    <ProtectedRoute>
                        <Dashboard/>
                    </ProtectedRoute>
                </Route>
                <Route exact path="/app">
                    <ProtectedRoute>
                        <Totem/>
                    </ProtectedRoute>
                </Route>

                {/* Compatibilité ascendante : anciennes URLs /:ent/:id et /:ent */}
                <Route exact path="/:ent/:id">
                    <ProtectedRoute>
                        <Dashboard/>
                    </ProtectedRoute>
                </Route>
                <Route exact path="/:ent">
                    <ProtectedRoute>
                        <Totem/>
                    </ProtectedRoute>
                </Route>

                <Route exact path="/">
                    <ProtectedRoute>
                        <div />
                    </ProtectedRoute>
                </Route>
            </Switch>
        </BrowserRouter>
    );
}

export default App;
